import React, { useState, useEffect } from 'react';
import { LineChart, Line, ComposedChart, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { getPurchases } from '../services/firestoreService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';

// Convert Firestore Timestamps, ISO strings, or Dates to a JS Date
const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (value.seconds) return new Date(value.seconds * 1000);
  return null;
};

// When the purchase actually happened: Programs use startDate, A La Carte uses date
const getPurchaseDate = (purchase) => purchase.startDate || purchase.date || purchase.createdAt;

// When a lead entered the pipeline (persists after conversion to client)
const getLeadReceivedDate = (account) => account.receivedDate || account.createdAt;

// When an account became a client: explicit timestamps, then the
// "Converted to Client" status history entry, then contract start
const getClientSignedDate = (account) => {
  if (account.wonAt) return account.wonAt;
  if (account.convertedAt) return account.convertedAt;
  const conversion = (account.statusHistory || []).find(
    entry => typeof entry.action === 'string' && entry.action.startsWith('Converted to Client')
  );
  const conversionDate = conversion ? toDate(conversion.date) : null;
  return conversionDate || account.contractStart || account.createdAt;
};

const isWon = (account) =>
  account.type === 'Clients' || String(account.result || '').toLowerCase() === 'won';

const isLost = (account) => String(account.result || '').toLowerCase() === 'lost';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SalesReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [timeFilter, setTimeFilter] = useState('This Year');

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch purchases data
        const purchasesData = await getPurchases();
        setPurchases(purchasesData);

        // Fetch leads AND clients — converted leads change type to 'Clients',
        // so both are needed for close ratio and new-client counts.
        // No orderBy: it would silently exclude docs missing createdAt.
        const accountsQuery = query(
          collection(db, 'accounts'),
          where('type', 'in', ['Leads', 'Clients'])
        );
        const accountsSnapshot = await getDocs(accountsQuery);
        const accountsData = accountsSnapshot.docs.map(docSnap => {
          const d = docSnap.data();
          return {
            id: docSnap.id,
            ...d,
            createdAt: toDate(d.createdAt),
            receivedDate: toDate(d.receivedDate),
            lastActivityAt: toDate(d.lastActivityAt),
            lostAt: toDate(d.lostAt),
            wonAt: toDate(d.wonAt),
            convertedAt: toDate(d.convertedAt),
            contractStart: toDate(d.contractStart)
          };
        });
        setAccounts(accountsData);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Leads still in the pipeline vs. accounts that became clients
  const clients = accounts.filter(a => a.type === 'Clients');
  const pipelineLeads = accounts.filter(a => a.type === 'Leads');

  // Revenue-counting purchases: skip drafts and cancellations
  const countedPurchases = purchases.filter(p => p.status !== 'Cancelled' && p.status !== 'Draft');

  // Start of the selected time window (null = All Time, no lower bound)
  const getWindowStart = () => {
    if (timeFilter === 'All Time') return null;
    const now = new Date();
    const start = new Date();
    if (timeFilter === 'This Year') {
      start.setMonth(0, 1);
    } else if (timeFilter === 'This Quarter') {
      start.setMonth(Math.floor(now.getMonth() / 3) * 3, 1);
    } else if (timeFilter === 'This Month') {
      start.setDate(1);
    }
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const inWindow = (date) => {
    const windowStart = getWindowStart();
    return windowStart ? (date && date >= windowStart) : true;
  };

  // Chart buckets for the selected time filter. For All Time the year range
  // comes from the dates actually present in the data.
  const getPeriodBuckets = (datesForAllTime) => {
    const now = new Date();
    const year = now.getFullYear();

    if (timeFilter === 'This Quarter') {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      return [0, 1, 2].map(i => {
        const m = quarterStartMonth + i;
        return {
          period: MONTH_NAMES[m],
          start: new Date(year, m, 1),
          end: new Date(year, m + 1, 0, 23, 59, 59, 999)
        };
      });
    }

    if (timeFilter === 'This Month') {
      const month = now.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const weeks = Math.ceil(daysInMonth / 7);
      return Array.from({ length: weeks }, (_, i) => ({
        period: `Week ${i + 1}`,
        start: new Date(year, month, i * 7 + 1),
        end: new Date(year, month, Math.min((i + 1) * 7, daysInMonth), 23, 59, 59, 999)
      }));
    }

    if (timeFilter === 'All Time') {
      const years = [...new Set(
        (datesForAllTime || []).filter(Boolean).map(d => d.getFullYear())
      )].sort();
      return years.map(y => ({
        period: String(y),
        start: new Date(y, 0, 1),
        end: new Date(y, 11, 31, 23, 59, 59, 999)
      }));
    }

    // This Year — Jan through Dec
    return Array.from({ length: 12 }, (_, m) => ({
      period: MONTH_NAMES[m],
      start: new Date(year, m, 1),
      end: new Date(year, m + 1, 0, 23, 59, 59, 999)
    }));
  };

  const inBucket = (date, bucket) => date && date >= bucket.start && date <= bucket.end;

  // Revenue + new clients signed per period
  const calculateRevenueData = () => {
    const buckets = getPeriodBuckets([
      ...countedPurchases.map(getPurchaseDate),
      ...clients.map(getClientSignedDate)
    ]);

    return buckets.map(bucket => {
      const bucketPurchases = countedPurchases.filter(p => inBucket(getPurchaseDate(p), bucket));
      return {
        period: bucket.period,
        revenue: bucketPurchases.reduce((sum, p) => sum + (p.total || 0), 0),
        deals: bucketPurchases.length,
        newClients: clients.filter(c => inBucket(getClientSignedDate(c), bucket)).length
      };
    });
  };

  const calculateSalesByProgram = () => {
    const filteredPurchases = countedPurchases.filter(p => inWindow(getPurchaseDate(p)));
    const programPurchases = filteredPurchases.filter(p => p.type === 'Programs' && p.program);
    const programCounts = {};
    const programRevenue = {};
    
    programPurchases.forEach(purchase => {
      const program = purchase.program;
      programCounts[program] = (programCounts[program] || 0) + 1;
      programRevenue[program] = (programRevenue[program] || 0) + (purchase.total || 0);
    });
    
    const totalPrograms = Object.keys(programCounts).length;
    const colors = ['#EF4444', '#7C3AED', '#F59E0B', '#10B981', '#EC4899'];
    
    return Object.keys(programCounts).map((program, index) => ({
      name: program,
      value: totalPrograms > 0 ? Math.round((programCounts[program] / programPurchases.length) * 100) : 0,
      revenue: programRevenue[program] || 0,
      color: colors[index % colors.length]
    }));
  };

  const calculateLeadStageDuration = () => {
    // Open pipeline leads only — won leads become Clients, lost leads have result 'Lost'
    const filteredLeads = pipelineLeads.filter(l => !l.result && inWindow(getLeadReceivedDate(l)));
    const stageDurations = {};
    const stageCounts = {};

    // Actual pipeline stage order from the Accounts kanban
    const stageOrder = ['Prospects', 'Lead Received', 'Discovery Call', 'Assessment', 'Onboarding Call', 'Proposal'];

    filteredLeads.forEach(lead => {
      if (lead.stage && stageOrder.includes(lead.stage)) {
        const stage = lead.stage;
        if (!stageDurations[stage]) {
          stageDurations[stage] = [];
          stageCounts[stage] = 0;
        }
        
        // Calculate days since creation (simplified - in real implementation you'd track stage changes)
        if (lead.createdAt) {
          const daysSinceCreation = Math.floor((new Date() - lead.createdAt) / (1000 * 60 * 60 * 24));
          stageDurations[stage].push(daysSinceCreation);
          stageCounts[stage]++;
        }
      }
    });
    
    return stageOrder
      .filter(stage => stageCounts[stage] > 0)
      .map(stage => ({
        stage,
        avgDays: Math.round(
          stageDurations[stage].reduce((sum, days) => sum + days, 0) / stageDurations[stage].length
        )
      }));
  };

  // Close ratio per period: of the leads received in that period (including
  // ones that have since converted to clients), how many were won
  const calculateCloseRatio = () => {
    const buckets = getPeriodBuckets(accounts.map(getLeadReceivedDate));

    return buckets.map(bucket => {
      const cohort = accounts.filter(a => inBucket(getLeadReceivedDate(a), bucket));
      const wonCount = cohort.filter(isWon).length;
      const closeRatio = cohort.length > 0 ? (wonCount / cohort.length) * 100 : 0;
      return {
        period: bucket.period,
        deals: cohort.length,
        closeRatio: Math.round(closeRatio * 10) / 10
      };
    });
  };

  const calculateLostLeads = () => {
    const lostLeads = accounts.filter(a =>
      isLost(a) && inWindow(a.lostAt || getLeadReceivedDate(a))
    );

    const reasonCounts = {};
    lostLeads.forEach(lead => {
      const reason = lead.resultReason || lead.substatus || lead.reason || lead.lostReason || 'Unknown Reason';
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });

    const totalLost = lostLeads.length;

    return Object.keys(reasonCounts).map(reason => ({
      reason,
      count: reasonCounts[reason],
      percentage: totalLost > 0 ? Math.round((reasonCounts[reason] / totalLost) * 100 * 10) / 10 : 0
    }));
  };

  // Get calculated data
  const monthlyRevenueData = calculateRevenueData();
  const salesByProduct = calculateSalesByProgram();
  const leadStageDuration = calculateLeadStageDuration();
  const closeRatioData = calculateCloseRatio();
  const lostLeadsData = calculateLostLeads();


  if (loading) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>Loading sales data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm/reports')} />
          <h1 className={`text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white`}>Sales Analytics</h1>
          <div className="flex items-center space-x-2">
            <select 
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className={`px-3 py-2 border rounded-md shadow-sm text-sm ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="All Time">All Time</option>
              <option value="This Year">This Year</option>
              <option value="This Quarter">This Quarter</option>
              <option value="This Month">This Month</option>
            </select>
          </div>
        </div>


        {/* Revenue Section */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
          <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Revenue & New Clients</h2>
          <div className="h-80">
            {monthlyRevenueData.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No revenue recorded</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Add client purchases to see revenue data</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis yAxisId="revenue" tickFormatter={(value) => `$${value.toLocaleString()}`} />
                  <YAxis yAxisId="clients" orientation="right" allowDecimals={false} />
                  <Tooltip formatter={(value, name) => (
                    name === 'Revenue' ? [`$${Number(value).toLocaleString()}`, name] : [value, name]
                  )} />
                  <Legend />
                  <Bar yAxisId="clients" dataKey="newClients" name="New Clients" fill="#3B82F6" barSize={24} radius={[4, 4, 0, 0]} />
                  <Line yAxisId="revenue" type="monotone" dataKey="revenue" name="Revenue" stroke="#EF4444" strokeWidth={3} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Sales Distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sales by Program</h2>
            <div className="h-96">
              {salesByProduct.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No sales recorded</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Add client purchases to see breakdown</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={salesByProduct}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={140}
                      label
                    >
                      {salesByProduct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [`${value}%`, name]} />
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Lead Stage Duration</h2>
            <div className="h-[500px]">
              {leadStageDuration.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No leads recorded</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Add leads to see stage duration data</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={leadStageDuration} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="stage" type="category" width={120} />
                    <Tooltip 
                      formatter={(value, name) => [`${value} days`, name]}
                      labelFormatter={(label) => `${label} Stage`}
                    />
                    <Legend />
                    <Bar dataKey="avgDays" name="Average Days" fill="#EF4444" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Close Ratio and Lost Leads */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Close Ratio</h2>
            <div className="h-96">
              {closeRatioData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No leads recorded</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Add leads to see close ratio data</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={closeRatioData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip formatter={(value, name) => [`${value}${name === 'closeRatio' ? '%' : ''}`, name === 'closeRatio' ? 'Close Ratio' : 'Deals']} />
                    <Legend />
                    <Line type="monotone" dataKey="closeRatio" name="Close Ratio" stroke="#10B981" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Lost Leads</h2>
            <div className="h-96">
              {lostLeadsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={lostLeadsData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="reason" type="category" width={150} />
                  <Tooltip 
                    formatter={(value) => [`${value} leads`, 'Count']}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Bar dataKey="count" name="Number of Leads" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="text-lg font-medium mb-2">No Lost Leads</div>
                    <div className="text-sm">No leads have been marked as lost yet</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>




      </div>
    </div>
  );
};

export default SalesReports; 